import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Flower2, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

function RegisterPage({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  const sessionId = searchParams.get('session_id');
  const packageId = searchParams.get('package');

  useEffect(() => {
    // Verify payment session exists
    if (!sessionId || !packageId) {
      toast.error('Invalid registration link. Please purchase a plan first.');
      navigate('/pricing');
      return;
    }
    
    // Payment verified by Stripe redirect
    setPaymentVerified(true);
    setVerifying(false);
  }, [sessionId, packageId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register-after-payment`, {
        email,
        password,
        display_name: displayName,
        session_id: sessionId,
        package_id: packageId
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('display_name', response.data.display_name);
      localStorage.setItem('user_id', response.data.user_id);

      toast.success(`Welcome to Radiant 40, ${displayName}!`);
      setIsAuthenticated(true);
      navigate('/welcome');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-secondary-700 text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (!paymentVerified) {
    return null;
  }

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-10">
          {/* Success Banner */}
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-8 text-center">
            <div className="flex items-center justify-center gap-2 text-green-700">
              <Check className="w-6 h-6" />
              <span className="font-semibold">Payment Successful!</span>
            </div>
            <p className="text-sm text-green-600 mt-1">Now create your account to begin</p>
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
              <Flower2 className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-4xl font-serif font-bold text-secondary-900 mb-2">
              Create Your Account
            </h2>
            <p className="text-secondary-600">
              One last step to begin your transformation
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div>
              <Label htmlFor="displayName" className="text-secondary-900 font-medium mb-2 block">
                Your Name
              </Label>
              <Input
                id="displayName"
                data-testid="display-name-input"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="What should we call you?"
                required
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-secondary-900 font-medium mb-2 block">
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-secondary-900 font-medium mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                minLength={6}
                className="w-full px-4 py-6 rounded-xl border-2 border-primary/20 focus:border-primary transition-colors"
              />
            </div>

            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {loading ? 'Creating Account...' : 'Start My Journey'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-secondary-600 mb-2">Already have an account?</p>
            <Button
              data-testid="login-link-button"
              onClick={() => navigate('/auth')}
              variant="ghost"
              className="text-primary hover:text-primary/80 font-semibold"
            >
              Sign In Instead
            </Button>
          </div>

          <p className="text-center text-sm text-secondary-500 mt-4">
            By creating an account, you agree to our{' '}
            <button 
              type="button"
              onClick={() => navigate('/terms')}
              className="text-primary hover:underline"
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button 
              type="button"
              onClick={() => navigate('/privacy')}
              className="text-primary hover:underline"
            >
              Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
